/**
 * ExpandableFlightLeg Component
 * Collapsible card showing flight leg details with FBO info and action buttons
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/utils/cn';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  MapPinIcon,
  PhoneIcon,
  ClockIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import { Flight } from '@/services/tripService';
import {
  getGoogleMapsUrl,
  getUberDeepLink,
  formatPhoneNumber,
  getPhoneLink,
} from '@/utils/fboHelpers';

interface FBOInfo {
  name: string;
  address?: string;
  phone?: string;
  notes?: string;
}

interface ExpandableFlightLegProps {
  flight: Flight;
  legNumber: number;
  isExpanded: boolean;
  onToggle: () => void;
  tripId: number;
  tripStatus: string;
  fboInfo?: {
    departure?: FBOInfo;
    arrival?: FBOInfo;
  };
}

const statusColors: Record<string, string> = {
  PENDING_REVIEW: 'bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-700',
  IN_PROGRESS: 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-700',
  SUBMITTED: 'bg-purple-100 text-purple-800 border-purple-300 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-700',
  APPROVED: 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-400 dark:border-green-700',
  PAID: 'bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-700',
};

const formatStatus = (status: string) => {
  return status
    .replace('_', ' ')
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const formatTime = (timeStr: string | null): string => {
  if (!timeStr) return '--:--';
  // Handle various time formats
  if (timeStr.includes(':')) {
    return timeStr.slice(0, 5); // "HH:MM"
  }
  return timeStr;
};

export const ExpandableFlightLeg: React.FC<ExpandableFlightLegProps> = ({
  flight,
  legNumber,
  isExpanded,
  onToggle,
  tripId,
  tripStatus,
  fboInfo,
}) => {
  const navigate = useNavigate();

  const showEnterDataButton =
    (tripStatus === 'PENDING_REVIEW' || tripStatus === 'IN_PROGRESS') &&
    (flight.status === 'PENDING_REVIEW' || flight.status === 'IN_PROGRESS');

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      {/* Collapsed Header - Always Visible */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-muted-foreground">
            Leg {legNumber}
          </span>
          <span className="text-lg font-semibold text-foreground">
            {formatTime(flight.estimatedDeparture)}
          </span>
          <div className="flex items-center gap-1 text-foreground">
            <span className="font-medium">{flight.fromAirport || '???'}</span>
            <PaperAirplaneIcon className="w-4 h-4 text-muted-foreground rotate-90" />
            <span className="font-medium">{flight.toAirport || '???'}</span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <span
            className={cn(
              'px-2 py-0.5 rounded-full text-xs font-medium border',
              statusColors[flight.status] || 'bg-muted text-muted-foreground border-border'
            )}
          >
            {formatStatus(flight.status)}
          </span>
          {isExpanded ? (
            <ChevronUpIcon className="w-5 h-5 text-muted-foreground" />
          ) : (
            <ChevronDownIcon className="w-5 h-5 text-muted-foreground" />
          )}
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-border">
          {/* Flight Details Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 py-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Departure</p>
              <p className="font-medium text-foreground">
                {formatTime(flight.estimatedDeparture)}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Arrival</p>
              <p className="font-medium text-foreground">
                {formatTime(flight.estimatedArrival)}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">ETE</p>
              <p className="font-medium text-foreground">
                {flight.estimatedEte || '--'}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Distance</p>
              <p className="font-medium text-foreground">
                {flight.distanceNm ? `${flight.distanceNm} NM` : '--'}
              </p>
            </div>
          </div>

          {/* Aircraft & Passengers */}
          <div className="flex gap-6 py-2 text-sm">
            {flight.tailNumber && (
              <div>
                <span className="text-muted-foreground">Aircraft: </span>
                <span className="font-medium text-foreground">
                  {flight.tailNumber}
                  {flight.aircraftType && (
                    <span className="text-muted-foreground ml-1">
                      ({flight.aircraftType})
                    </span>
                  )}
                </span>
              </div>
            )}
            {flight.passengers !== null && flight.passengers !== undefined && (
              <div>
                <span className="text-muted-foreground">Passengers: </span>
                <span className="font-medium text-foreground">{flight.passengers}</span>
              </div>
            )}
          </div>

          {/* FBO Info - Departure */}
          {fboInfo?.departure && (
            <div className="mt-4 p-3 bg-muted rounded-lg">
              <p className="text-xs font-medium text-muted-foreground mb-2">
                Departure FBO - {flight.fromAirport}
              </p>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-foreground">{fboInfo.departure.name}</p>
                  {fboInfo.departure.address && (
                    <p className="text-sm text-muted-foreground">
                      {fboInfo.departure.address}
                    </p>
                  )}
                  {fboInfo.departure.notes && (
                    <p className="text-sm text-muted-foreground italic mt-1">
                      {fboInfo.departure.notes}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  {fboInfo.departure.phone && (
                    <a
                      href={getPhoneLink(fboInfo.departure.phone)}
                      className="p-2 bg-card rounded-lg border border-border hover:bg-muted transition-colors"
                      title={`Call ${formatPhoneNumber(fboInfo.departure.phone)}`}
                    >
                      <PhoneIcon className="w-5 h-5 text-muted-foreground" />
                    </a>
                  )}
                  {fboInfo.departure.address && (
                    <>
                      <a
                        href={getGoogleMapsUrl(fboInfo.departure.address)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 bg-card rounded-lg border border-border hover:bg-muted transition-colors"
                        title="Open in Maps"
                      >
                        <MapPinIcon className="w-5 h-5 text-muted-foreground" />
                      </a>
                      <a
                        href={getUberDeepLink(fboInfo.departure.address)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-2 bg-foreground text-background text-sm font-medium rounded-lg hover:bg-foreground/90 transition-colors"
                      >
                        Uber
                      </a>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* FBO Info - Arrival */}
          {fboInfo?.arrival && (
            <div className="mt-3 p-3 bg-muted rounded-lg">
              <p className="text-xs font-medium text-muted-foreground mb-2">
                Arrival FBO - {flight.toAirport}
              </p>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-foreground">{fboInfo.arrival.name}</p>
                  {fboInfo.arrival.address && (
                    <p className="text-sm text-muted-foreground">
                      {fboInfo.arrival.address}
                    </p>
                  )}
                  {fboInfo.arrival.notes && (
                    <p className="text-sm text-muted-foreground italic mt-1">
                      {fboInfo.arrival.notes}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  {fboInfo.arrival.phone && (
                    <a
                      href={getPhoneLink(fboInfo.arrival.phone)}
                      className="p-2 bg-card rounded-lg border border-border hover:bg-muted transition-colors"
                      title={`Call ${formatPhoneNumber(fboInfo.arrival.phone)}`}
                    >
                      <PhoneIcon className="w-5 h-5 text-muted-foreground" />
                    </a>
                  )}
                  {fboInfo.arrival.address && (
                    <>
                      <a
                        href={getGoogleMapsUrl(fboInfo.arrival.address)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 bg-card rounded-lg border border-border hover:bg-muted transition-colors"
                        title="Open in Maps"
                      >
                        <MapPinIcon className="w-5 h-5 text-muted-foreground" />
                      </a>
                      <a
                        href={getUberDeepLink(fboInfo.arrival.address)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-2 bg-foreground text-background text-sm font-medium rounded-lg hover:bg-foreground/90 transition-colors"
                      >
                        Uber
                      </a>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Actual Times (if completed) */}
          {(flight.actualAirTime || flight.actualFlightTime) && (
            <div className="mt-4 p-3 bg-success/10 rounded-lg">
              <p className="text-xs font-medium text-success mb-2">
                Actual Times
              </p>
              <div className="flex gap-6">
                {flight.actualAirTime !== null && (
                  <div>
                    <span className="text-success/80">Air Time: </span>
                    <span className="font-medium text-success">
                      {flight.actualAirTime.toFixed(1)} hrs
                    </span>
                  </div>
                )}
                {flight.actualFlightTime !== null && (
                  <div>
                    <span className="text-success/80">Flight Time: </span>
                    <span className="font-medium text-success">
                      {flight.actualFlightTime.toFixed(1)} hrs
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Pilot Notes */}
          {flight.pilotNotes && (
            <div className="mt-3 p-3 bg-primary/10 rounded-lg">
              <p className="text-xs font-medium text-primary mb-1">Notes</p>
              <p className="text-sm text-foreground">{flight.pilotNotes}</p>
            </div>
          )}

          {/* Action Button */}
          {showEnterDataButton && (
            <button
              onClick={() => navigate(`/trips/${tripId}/wizard`)}
              className="mt-4 w-full py-3 px-4 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <ClockIcon className="w-5 h-5" />
              Enter Flight Data
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ExpandableFlightLeg;
